/**
 * @module ShipStationOIDCProviderAdapter
 * @description OIDC provider adapter for ShipStation's custom OIDC provider.
 * Implements OIDC authorization code flow with PKCE (RFC 7636) using direct fetch calls.
 */

/* eslint-disable import/extensions */
import { OIDCProviderAdapter } from './oidc-provider-adapter.mjs';
import { PROVIDER_SSAS } from './constants.mjs';

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'ssas_access_token',
  ID_TOKEN: 'ssas_id_token',
  REFRESH_TOKEN: 'ssas_refresh_token',
  EXPIRES_AT: 'ssas_expires_at',
  STATE: 'ssas_state',
  CODE_VERIFIER: 'ssas_code_verifier',
};

const SSAS_IS_AUTH_COOKIE = 'ssas.is.authenticated';

/**
 * ShipStation OIDC provider adapter.
 * Uses OIDC authorization code flow with PKCE for secure client-side token exchange.
 */
class ShipStationOIDCProviderAdapter extends OIDCProviderAdapter {
  /**
   * @param {Object} config - OIDC configuration
   * @param {string} config.issuerUrl - The OIDC issuer URL (e.g., http://localhost:4000/oidc)
   * @param {string} config.clientId - The OIDC client ID
   * @param {string} [config.redirectUri] - The redirect URI after authentication
   * @param {string} config.scope - The OIDC scopes to request
   */
  constructor(config) {
    super(config);
    this.issuerUrl = null;
    this.clientId = null;
    this.redirectUri = null;
    this.scope = config.scope;
    this.endpoints = null;
    this.providerType = PROVIDER_SSAS;
  }

  /**
   * Initialize the OIDC client by fetching the discovery document.
   * @returns {Promise<this>} The initialized adapter
   */
  async createClient() {
    this.issuerUrl = this.config.issuerUrl;
    this.clientId = this.config.clientId;

    // Strip query parameters from redirect URI for clean OIDC provider matching
    const rawRedirectUri = this.config.redirectUri || `${window.location.origin}/loginauth`;
    this.redirectUri = this._stripQueryParams(rawRedirectUri);

    console.log(`ShipStationOIDCProviderAdapter: Creating client for issuer: ${this.issuerUrl}`);

    // Fetch OIDC discovery document
    const discoveryUrl = `${this.issuerUrl}/.well-known/openid-configuration`;
    const response = await fetch(discoveryUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch OIDC discovery document from ${discoveryUrl}: ${response.status}`);
    }

    this.endpoints = await response.json();
    console.log(`ShipStationOIDCProviderAdapter: Discovered endpoints: ${JSON.stringify(this.endpoints)}`);

    return this;
  }

  /**
   * Initiate login with redirect using OIDC authorization code flow with PKCE.
   * @param {Object} options - Login options
   * @param {Object} [options.authorizationParams] - Additional authorization parameters
   * @param {Object} [options.appState] - App state to preserve through the auth flow
   * @returns {Promise<void>}
   */
  async loginWithRedirect(options = {}) {
    const state = this._generateRandomString(32);

    // Generate PKCE code verifier and challenge
    const codeVerifier = this._generateCodeVerifier();
    const codeChallenge = await this._generateCodeChallenge(codeVerifier);

    // Store state and code verifier for callback validation
    sessionStorage.setItem(STORAGE_KEYS.STATE, state);
    sessionStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);

    if (options.appState) {
      sessionStorage.setItem('ssas_app_state', JSON.stringify(options.appState));
    }
    const rawRedirect = options.authorizationParams?.redirect_uri || this.redirectUri;

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this._stripQueryParams(rawRedirect),
      response_type: 'code',
      scope: this.scope,
      state,
      resource: 'ss:webapi',
      prompt: 'consent',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    if (options.authorizationParams) {
      Object.entries(options.authorizationParams).forEach(([key, value]) => {
        if (key !== 'redirect_uri' && value !== undefined && value !== null) {
          params.set(key, String(value));
        }
      });
    }

    const authUrl = `${this.endpoints.authorization_endpoint}?${params.toString()}`;
    console.log(`ShipStationOIDCProviderAdapter: Redirecting to: ${authUrl}`);

    window.location.href = authUrl;
  }

  /**
   * Handle the callback after login redirect.
   * Exchanges the authorization code for tokens using PKCE.
   * @returns {Promise<{appState: Object}>} The preserved app state
   */
  async handleRedirectCallback() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    console.log('ShipStationOIDCProviderAdapter: handleRedirectCallback');

    // Check for errors from the authorization server
    if (error) {
      const errorMessage = errorDescription || error;
      console.error(`ShipStationOIDCProviderAdapter: Authorization error: ${errorMessage}`);
      this._cleanupAuthSession();
      throw new Error(errorMessage);
    }

    // Verify state to prevent CSRF attacks
    const savedState = sessionStorage.getItem(STORAGE_KEYS.STATE);
    if (state !== savedState) {
      this._cleanupAuthSession();
      throw new Error('State mismatch - potential CSRF attack');
    }

    // Retrieve code verifier for PKCE
    const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
    if (!codeVerifier) {
      this._cleanupAuthSession();
      throw new Error('Code verifier not found - PKCE flow incomplete');
    }

    if (!code) {
      this._cleanupAuthSession();
      throw new Error('Authorization code not found in callback');
    }

    // Exchange authorization code for tokens using PKCE
    const tokens = await this._exchangeCodeForTokens(code, codeVerifier);
    this._storeTokens(tokens);

    // Clean up URL (remove code and state from URL)
    window.history.replaceState(null, '', window.location.pathname + window.location.hash);

    // Clean up session storage
    this._cleanupAuthSession();

    // Retrieve and clean up app state
    const appStateJson = sessionStorage.getItem('ssas_app_state');
    sessionStorage.removeItem('ssas_app_state');
    const appState = appStateJson ? JSON.parse(appStateJson) : {};

    return { appState };
  }

  /**
   * Clean up auth session storage items.
   */
  _cleanupAuthSession() {
    sessionStorage.removeItem(STORAGE_KEYS.STATE);
    sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
  }

  /**
   * Exchange authorization code for tokens using PKCE.
   * @param {string} code - The authorization code
   * @param {string} codeVerifier - The PKCE code verifier
   * @returns {Promise<Object>} The token response
   */
  async _exchangeCodeForTokens(code, codeVerifier) {
    console.log('ShipStationOIDCProviderAdapter: Exchanging code for tokens with PKCE');

    const response = await fetch(this.endpoints.token_endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        code,
        redirect_uri: this.redirectUri,
        client_id: this.clientId,
        code_verifier: codeVerifier,
        resource: 'ss:webapi',
      }),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error(`ShipStationOIDCProviderAdapter: Token exchange failed: ${errorBody}`);
      throw new Error(`Token exchange failed: ${response.status}`);
    }

    const tokens = await response.json();
    console.log('ShipStationOIDCProviderAdapter: Token exchange successful');
    return tokens;
  }

  /**
   * Check if the user is authenticated.
   * @returns {Promise<boolean>} True if authenticated with valid token
   */
  async isAuthenticated() {
    const token = this._getStoredToken();
    const isAuth = token && !this._isTokenExpired();
    console.log(`ShipStationOIDCProviderAdapter: isAuthenticated => ${isAuth}`);
    return isAuth;
  }

  /**
   * Get the stored access token.
   * @returns {Promise<string|null>} The access token or null
   */
  async getTokenSilently() {
    console.log('ShipStationOIDCProviderAdapter: getToken');

    // Check if token is expired and we have a refresh token
    if (this._isTokenExpired()) {
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (refreshToken && this.endpoints?.token_endpoint) {
        try {
          await this._refreshAccessToken(refreshToken);
        } catch (error) {
          console.error('ShipStationOIDCProviderAdapter: Token refresh failed', error);
          return null;
        }
      } else {
        return null;
      }
    }

    return this._getStoredToken();
  }

  /**
   * Get the user profile from the cached ID token.
   * @returns {Promise<Object|null>} The user profile or null
   */
  async getUser() {
    console.log('ShipStationOIDCProviderAdapter: getUser');

    const idToken = localStorage.getItem(STORAGE_KEYS.ID_TOKEN);
    if (idToken) {
      return this._decodeJwt(idToken);
    }

    return null;
  }

  async logout() {
    // TODO: implement logout
    throw new Error('Logout for SSAS OIDC provider not implemented');
  }

  /**
   * Get the provider type identifier.
   * @returns {string} 'ssas'
   */
  getProviderType() {
    return PROVIDER_SSAS;
  }

  cleanAuthState() {
    this._clearStoredTokens();
  }

  /**
   * Enrich a user profile with additional IDP data.
   * For ShipStation OIDC, this is a passthrough as there's no additional IDP data to fetch.
   * @param {Object} userProfile - The base user profile
   * @returns {Promise<Object>} The user profile (unchanged)
   */
  async enrichUserProfile(userProfile) {
    console.log('ShipStationOIDCProviderAdapter: enrichUserProfile (passthrough)');
    // TODO: add profile enrichment for ssas
    return userProfile;
  }

  devUnlinkAllSsoAccounts() {
    throw new Error('unlink sso accounts not implemented in ssas');
  }

  // ============ Private Helper Methods ============

  /**
   * Strip query parameters from a URL.
   * @param {string} url - The URL to strip
   * @returns {string} URL without query parameters
   */
  _stripQueryParams(url) {
    try {
      const parsed = new URL(url);
      return `${parsed.origin}${parsed.pathname}`;
    } catch {
      // If URL parsing fails, try simple string manipulation
      const queryIndex = url.indexOf('?');
      return queryIndex > -1 ? url.substring(0, queryIndex) : url;
    }
  }

  /**
   * Generate a random string for state/nonce.
   * @param {number} length - Length of the string
   * @returns {string} Random string
   */
  _generateRandomString(length) {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('').slice(0, length);
  }

  /**
   * Generate a PKCE code verifier.
   * Per RFC 7636, code verifier must be 43-128 characters from [A-Z] / [a-z] / [0-9] / "-" / "." / "_" / "~"
   * @returns {string} A random code verifier string
   */
  _generateCodeVerifier() {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return this._base64UrlEncode(array);
  }

  /**
   * Generate a PKCE code challenge from a code verifier using SHA-256.
   * @param {string} codeVerifier - The code verifier to hash
   * @returns {Promise<string>} The base64url-encoded SHA-256 hash
   */
  async _generateCodeChallenge(codeVerifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this._base64UrlEncode(new Uint8Array(digest));
  }

  /**
   * Base64url encode a Uint8Array (RFC 4648 Section 5).
   * @param {Uint8Array} buffer - The buffer to encode
   * @returns {string} Base64url encoded string
   */
  _base64UrlEncode(buffer) {
    const base64 = btoa(String.fromCharCode(...buffer));
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  /**
   * Store tokens in localStorage and set authentication cookie.
   * @param {Object} tokens - Token response
   */
  _storeTokens(tokens) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);

    if (tokens.id_token) {
      localStorage.setItem(STORAGE_KEYS.ID_TOKEN, tokens.id_token);
    }

    if (tokens.refresh_token) {
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
    }

    if (tokens.expires_in) {
      const expiresAt = Date.now() + (parseInt(tokens.expires_in, 10) * 1000);
      localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, expiresAt.toString());
    }

    // Set cookie for server-side auth check (matching the pattern expected by ss-ui-node)
    document.cookie = `${SSAS_IS_AUTH_COOKIE}=true; path=/`;

    console.log('ShipStationOIDCProviderAdapter: Tokens stored');
  }

  /**
   * Get the stored access token.
   * @returns {string|null} The access token or null
   */
  _getStoredToken() {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Clear all stored tokens.
   */
  _clearStoredTokens() {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);

    // Clear authentication cookie
    document.cookie = `${SSAS_IS_AUTH_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

    console.log('ShipStationOIDCProviderAdapter: Tokens cleared');
  }

  /**
   * Check if the stored token is expired.
   * @returns {boolean} True if expired or no expiration info
   */
  _isTokenExpired() {
    const expiresAt = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
    if (!expiresAt) {
      // If no expiration stored, assume not expired
      return false;
    }
    // Add 60 second buffer to account for clock skew
    return Date.now() > (parseInt(expiresAt, 10) - 60000);
  }

  /**
   * Refresh the access token using the refresh token.
   * @param {string} refreshToken - The refresh token
   * @returns {Promise<void>}
   */
  async _refreshAccessToken(refreshToken) {
    console.log('ShipStationOIDCProviderAdapter: Refreshing access token');

    const response = await fetch(this.endpoints.token_endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: this.clientId,
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error(`ShipStationOIDCProviderAdapter: Token refresh failed: ${errorBody}`);
      this._clearStoredTokens();
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    const tokens = await response.json();
    this._storeTokens(tokens);
  }

  /**
   * Decode a JWT token to get its payload.
   * @param {string} token - The JWT token
   * @returns {Object|null} The decoded payload or null
   */
  _decodeJwt(token) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.error('ShipStationOIDCProviderAdapter: Failed to decode JWT', error);
      return null;
    }
  }
}

export { ShipStationOIDCProviderAdapter };
